import {AddSchema} from "mobx-graphlink";
import {BreathCompMode} from "./Components/BreathComp.js";
import {MPPromptActionType} from "./Components/MemoryPalaceComp.js";
import {EffectSource} from "./Components/AlertsComp.js";
import {Phase} from "../../Server/DB/@Shared/Phase.js";

export const SessionEventType_values = [
	// synthetic (not recorded in database, but frontend may add these to its client-side event-arrays on-the-fly)
	"General.SessionStart",
	"General.SessionRecovered",
	"General.SessionEnd",

	// general (added from outside of any specific comp, or added by the General comp)
	"General.InitialDelayEnd",
	// new "generic" event-type, for new generation of session-events (new approach just uses key of detail-struct as the event-type)
	"Generic",

	// breath
	"Breath.SirenStart",
	"Breath.DeildAttemptStart",

	// alarms
	"General.SleepCycleEnd",
	"Alarms.PhaseSkip", // atm, this event is only for skipping drifting-phase (will add old-phase field once comp-based approach used)
	"Alarms.PhaseSet",
	"Alarms.SequenceStart",
	"Alarms.SolveStart",
	"Alarms.SolveFail",
	"Alarms.SolveSuccess",
	"Alarms.GiveUpOnSolving",

	// (micro-)snooze
	"Snooze.SnoozeStart",
	"Snooze.SnoozeEnd",

	// dream-quiz
	"DreamQuiz.TargetHit",
	"DreamQuiz.TargetMiss",
	"DreamQuiz.TargetGiveUp",

	// sound-quiz
	"SoundQuiz.TargetHit",
	"SoundQuiz.TargetMiss",
	"SoundQuiz.TargetGiveUp",

	// concept-link
	"ConceptLink.TargetVoiced",
	"ConceptLink.TargetVisualized",

	// reality-check
	"RealityCheck.ReminderPlay",
	"RealityCheck.ReminderSkip", // recorded if time of reminder comes, but a condition (like max-ambient-noise) is not met
	"RealityCheck.ReminderHit",

	// signals
	"Signals.SignalPlay",

	// memory
	"MemoryPrompt.TargetHit",
	"MemoryPrompt.TargetMiss",
	"MemoryPrompt.TargetGiveUp",

	// memory palace
	"MemoryPalace.PromptPlay",
	"MemoryPalace.RecallResult",
	"MemoryPalace.DecodeFailed", // deprecated: use MemoryPalace.RecallResult instead

	// vr
	"VR.ImageFrameDisplay", // note: this event is only triggered the first time a given image-frame is displayed this session
	"VR.ImageFrameLieCatch",
	"VR.ImageFrameFalseAccuse",

	"Visualization.StartPrompting",
	"Visualization.StopPrompting",

	"Media.Play",
	"Media.Pause",
	"Media.MarkDesyncCaught",

	// events independent of specific components
	"EffectPointer.Play",
] as const;
export type SessionEventType = typeof SessionEventType_values[number];
AddSchema("SessionEventType", {oneOf: SessionEventType_values.map(a=>({const: a}))});

// subgroups
export const eventTypes_userInteractions: SessionEventType[] = [
	"Alarms.SolveStart",
	"ConceptLink.TargetVoiced", "ConceptLink.TargetVisualized",
	"RealityCheck.ReminderHit",
	// this list is NOT EXHAUSTIVE atm; just what's needed atm by GetTimelineEventsIntersectingRange_SleepScheduleMode
];
export const eventTypes_dreamQuiz: SessionEventType[] = ["DreamQuiz.TargetHit", "DreamQuiz.TargetMiss", "DreamQuiz.TargetGiveUp"];
export const eventTypes_dreamQuiz_promptEnder: SessionEventType[] = ["DreamQuiz.TargetHit", "DreamQuiz.TargetGiveUp"];
export const eventTypes_soundQuiz: SessionEventType[] = ["SoundQuiz.TargetHit", "SoundQuiz.TargetMiss", "SoundQuiz.TargetGiveUp"];
export const eventTypes_soundQuiz_promptEnder: SessionEventType[] = ["SoundQuiz.TargetHit", "SoundQuiz.TargetGiveUp"];
export const eventTypes_dreamOrSoundQuiz_promptEnder: SessionEventType[] = [...eventTypes_dreamQuiz_promptEnder, ...eventTypes_soundQuiz_promptEnder];

export class SessionEvent {
	constructor(initialData: Partial<SessionEvent>) {
		Object.assign(this, {date: Date.now()}, initialData);
	}

	// this field marks an event as "synthetic/injected" helper-event (ie. it wasn't present in the actual db)
	_isSynthetic?: boolean;

	date: number;
	type: SessionEventType;
	/** For marking events as invalid (eg. if created in error and should be ignored in charts and such). */
	invalidated?: boolean|n;

	// event-detail structures (old gen)
	// ==========

	// if Alarms.SequenceStart
	alarmSequence?: string;

	// if Breath.SirenStart
	sirenConfig?: SessionEvent_SirenConfig;

	// if Alarms.PhaseSet
	phase?: Phase;

	// if RealityCheck.ReminderPlay or RealityCheck.ReminderSkip
	ambientNoise?: number; // useful for if user wants to view stats using a different threshold in the future

	// if Signals.SignalPlay
	signalName?: string;

	// if MemoryPalace.PromptPlay
	memoryPalace_promptPlay?: {type: MPPromptActionType, target: string};

	// if MemoryPalace.RecallResult
	memoryPalace_recallResult?: MemoryPalace_RecallResult;

	// if MemoryPalace.DecodeFailed (deprecated: use memoryPalace_recallResult instead)
	memoryPalace_decodeFailed?: {target: string, timeSinceEncode: number};

	// if Visualization.StartPrompting
	visualization_startPrompting?: {promptInterval: number}; // (promptInterval might not be exact; see EngineConfig_Visualization.promptInterval comments)

	// if Media.Play // commented; might add this later
	//media_play?: {soundFile_filePath: string|null};

	// if Media.MarkDesyncCaught
	media_markDesyncCaught?: {
		// if user signaled a desync-caught, but no desync was actually active, both of these will be null
		timeSinceDesyncStart: number|null,
		mediaTimeSinceDesyncStart: number|null,
	};

	// if EffectPointer.Play
	effectPointer_play?: {
		// if type "sound-file"
		soundFile_filePath?: string|n,
		soundFile_startTime?: number|n, // in seconds
	};

	// event-detail structures (new gen, ie. where the structure-key is itself the "event type")
	// ==========

	/** Note: This session-event is only emitted when the source-state *changes between active<>inactive*. */
	alerts_setAlertActive?: {source: EffectSource, active: boolean};
}
AddSchema("SessionEvent", {
	properties: {
		date: {type: "number"},
		type: {$ref: "SessionEventType"},
		invalidated: {type: ["null", "boolean"]},
		alarmSequence: {type: ["null", "string"]},
		sirenConfig: {
			type: ["null", "object"],
			properties: {
				cycleTime: {type: "number"}, // in seconds
			},
		},
		phase: {$ref: "Phase"},
		ambientNoise: {type: ["null", "number"]},
		signalName: {type: ["null", "string"]},
		memoryPalace_promptPlay: {
			type: ["null", "object"],
			properties: {
				type: {type: "string", enum: ["find", "move"]},
				target: {type: "string"},
			},
		},
		memoryPalace_recallResult: {
			type: ["null", "object"],
			properties: {
				target: {type: "string"},
				timeSinceEncode: {type: "number"},
				decodeAttemptTime: {type: "number"},
				decodeSuccess: {type: "boolean"},
				encodeNextTime: {type: "number"},
			},
		},
		memoryPalace_decodeFailed: {
			type: ["null", "object"],
			properties: {
				target: {type: "string"},
				timeSinceEncode: {type: "number"},
			},
		},
		visualization_startPrompting: {
			type: ["null", "object"],
			properties: {
				promptInterval: {type: "number"},
			},
		},
		media_desyncCaught: {
			type: ["null", "object"],
			properties: {
				timeSinceDesyncStart: {type: ["null", "number"]},
				mediaTimeSinceDesyncStart: {type: ["null", "number"]},
			},
		},
		effectPointer_play: {
			type: ["null", "object"],
			properties: {
				soundFile_filePath: {type: ["null", "string"]},
			},
		},
		alerts_setAlertActive: {
			type: ["null", "object"],
			properties: {
				source: {$ref: "EffectSource"},
				active: {type: "boolean"},
			},
		},
	},
});

export type SessionEvent_SirenConfig = {
	cycleTime: number; // in seconds
};

// similar (but not same) structure as MemoryPalacePromptInfo
export type MemoryPalace_RecallResult = {
	target: string;
	decodeSuccess: boolean;

	encodeToDecodeDelay?: number;
	decodeAttemptDuration?: number;
	encodeNextDuration?: number;
};